import { Injectable } from '@angular/core';
import Keycloak, { KeycloakInitOptions } from 'keycloak-js';
import { forkJoin, from, Observable, Observer, of } from 'rxjs';
import { mergeMap, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import KeycloakAuthorization from 'keycloak-js/dist/keycloak-authz';
import { Router } from '@angular/router';
import { tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  private keycloak?: Keycloak;
  private authorization?: KeycloakAuthorization;
  public isLoggingOut = false;

  constructor(private router: Router) {

  }

  get isAuthenticated(): boolean {
    return this.keycloak?.authenticated ?? false;
  }

  get username(): string | undefined {
    return this.keycloak?.profile?.username;
  }

  get userEmail(): string | undefined {
    return this.keycloak?.profile?.email;
  }

  get userAccessToken(): string | undefined {
    return this.keycloak?.token;
  }

  get userIdToken(): string | undefined {
    return this.keycloak?.idToken;
  }

  get userSubject(): string | undefined {
    return this.keycloak?.subject;
  }

  get userRefreshToken(): string | undefined {
    return this.keycloak?.refreshToken;
  }

  get userSessionID(): string | undefined {
    return this.keycloak?.sessionId;
  }

  public init(): Observable<any> {
    const openIdConnectOptions = {
      url: environment.authServerUrl,
      realm: environment.realm,
      clientId: environment.clientId,
    };

    this.keycloak = new Keycloak(openIdConnectOptions);
    const initKeycloak = this.initKeycloak(this.keycloak)
      .pipe(
        switchMap(isAuthenticated => {
          if (isAuthenticated) {
            return forkJoin([
              this.initAuthorization(),
              this.loadUserProfile(isAuthenticated)
            ]);
          } else {
            // If not authenticated, skip loading user and auth init
            return of(null)
          }
        })
      );

    this.handleTokenExpiry();
    return initKeycloak;
  }

  handleTokenExpiry() {
    // Start - CUST-3716 - To fix the reload, updating the token when the token expired. - Dev: Arun Selvan C - 13/03/2024
    // Handle token expiration
    this.keycloak.onTokenExpired = () => {
      // Update the Keycloak instance's refresh token with the one in local storage.
      const storedRefreshToken = localStorage.getItem('RefreshToken');
      if (storedRefreshToken) {
        this.keycloak.refreshToken = storedRefreshToken;
      }

      if (this.keycloak?.refreshToken) {
        this.keycloak.updateToken(30).then(refreshed => {
          if (refreshed) {
            this.updateTokenInLocalStorage();
          } else {
            this.login();
          }
        }).catch(error => {
          console.error('Failed to refresh token', error);
          this.login();
        });
      } else {
        this.login();
      }
    };
  }

  public getAccessToken(): Observable<string> {
    if (!this.keycloak)
      throw Error('Keycloak authentication service not initialized');

    const acToken = localStorage.getItem('AccessToken');
    const isTokenExpiring = this.keycloak.isTokenExpired(120);
    if (acToken && !isTokenExpiring) {
      return of(acToken);
    } else {
      const isRefreshing = localStorage.getItem('refresh_lock');
      if (isRefreshing) {
        // Another tab is refreshing the token. Wait and check for the new token periodically.
        return new Observable<string>((observer) => {
          const intervalId = setInterval(() => {
            const newToken = localStorage.getItem('AccessToken');
            if (newToken && !this.keycloak.isTokenExpired(120)) {
              clearInterval(intervalId);
              observer.next(newToken);
              observer.complete();
            }
          }, 1000); // Check every second
        });
      } else {
        // No other tab is refreshing the token. Proceed to refresh.
        const storedToken = localStorage.getItem('AccessToken');
        const storedRefreshToken = localStorage.getItem('RefreshToken');
        if (storedToken && !this.keycloak.isTokenExpired(120) && storedRefreshToken) {
          // A valid token and refresh token are already in local storage. Use them instead of refreshing.
          this.keycloak.token = storedToken;
          this.keycloak.refreshToken = storedRefreshToken;
          return of(storedToken);
        } else {
          // Update the Keycloak instance's refresh token with the one in local storage.
          if (storedRefreshToken) {
            this.keycloak.refreshToken = storedRefreshToken;
          }

          localStorage.setItem('refresh_lock', 'true');
          return from(this.keycloak.updateToken(120))
            .pipe(
              tap(() => {
                // Update the token and refresh token in local storage.
                localStorage.setItem('AccessToken', this.keycloak.token);
                localStorage.setItem('RefreshToken', this.keycloak.refreshToken);
                localStorage.setItem('IdToken', this.keycloak?.idToken || '');
                localStorage.removeItem('refresh_lock'); // Remove the lock after refreshing
              }),
              mergeMap(() => this.getRptToken())
            );
        }
      }
    }
  }

  public getSignalRAccessToken(): Observable<string> {
    const storedSignalRToken = localStorage.getItem('AccessToken');
    if (storedSignalRToken) {
      return of(storedSignalRToken);
    }

    return of('');
  }

  public login(): void {
    if (!this.keycloak)
      throw Error('Keycloak authentication service not initialized');
    this.keycloak.login();
  }

  public logout(): void {
    if (!this.keycloak)
      throw Error('Keycloak authentication service not initialized');

    this.isLoggingOut = true;
    const logoutOptions = { redirectUri: environment.apiClientPath };

    localStorage.clear();
    sessionStorage.clear();
    this.keycloak.logout(logoutOptions).catch((error) => {
      this.isLoggingOut = false;
      console.log('Logout failed', error);
    });
  }

  private initKeycloak(keycloak: Keycloak): Observable<any> {
    const initOptions: KeycloakInitOptions = {
      onLoad: 'check-sso',
      silentCheckSsoRedirectUri: window.location.origin + '/assets/silent-check-sso.html',
      checkLoginIframe: false,
      pkceMethod: 'S256',
      enableLogging: false,
      flow: 'standard'
    };
    return from(
      keycloak.init(initOptions).then(async (authenticated) => {
        if (!authenticated) {
          await keycloak.login(); // only when user is not authenticated
        }
        return authenticated;
      })
    );
  }

  private initAuthorization(): Observable<void> {
    if (!this.keycloak)
      throw Error('Keycloak authentication service not initialized');

    this.authorization = new KeycloakAuthorization(this.keycloak);
    this.authorization.init();
    return of(void 0);
  }

  private loadUserProfile(isAuthenticated: boolean): Observable<any> {
    return isAuthenticated ? from(this.keycloak!.loadUserProfile()) : of(undefined);
  }

  private handleTokenExpiration() {
    // Perform any additional cleanup if needed

    // Redirect to the login page
    this.logout();
    this.router.navigate(['/']);
  }

  private getRptToken(): Observable<string> {
    return new Observable((observer: Observer<string>) => {
      if (!this.authorization)
        throw Error('Keycloak authorization service not initialized');

      this.authorization
        .entitlement(environment.resourceServerId)
        .then(
          rptToken => {
            localStorage.setItem('AccessToken', rptToken);
            observer.next(rptToken);
            observer.complete();
          },
          () => {
            this.handleTokenExpiration();
            observer.error('Authorization request was denied by the server.');
          },
          () => {
            this.handleTokenExpiration();
            observer.error('Could not obtain authorization data from the server.');
          }
        );
    });
  }

  private updateTokenInLocalStorage(): void {
    if (this.keycloak) {
      localStorage.setItem('AccessToken', this.keycloak.token || '');
      localStorage.setItem('RefreshToken', this.keycloak.refreshToken || '');
      localStorage.setItem('IdToken', this.keycloak.idToken || '');
    }
  }
}
